import React from "react";
import { motion } from "framer-motion";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faGithub,
  faLinkedin,
  faXTwitter,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";

import {
  faEnvelope,
  faFileLines,
  faArrowRight,
  faWandMagicSparkles,
  faCode,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";

// ==========================================
// DATA
// ==========================================

const quickLinks = [
  "Home",
  "Projects",
  "Experience",
  "Expertise",
  "Testimonials",
  "Contact",
];

const resources = [
  {
    name: "Project Ideas",
    soon: false,
    url: "https://jatinporiya.netlify.app/project-ideas.html",
  },
  {
    name: "Learning Resources",
    soon: true,
  },
  {
    name: "Case Studies",
    soon: true,
  },
  {
    name: "Blog",
    soon: true,
  },
  {
    name: "Tech Stack",
    soon: false,
    url: "#expertise",
  },
  {
    name: "Bookmarks",
    soon: true,
  },
];

const socialConnects = [
  {
    name: "GitHub",
    icon: faGithub,
    url: "https://github.com/interestingPlus/",
  },
  {
    name: "LinkedIn",
    icon: faLinkedin,
    url: "https://www.linkedin.com/in/jatin-poriya/",
  },
  {
    name: "Twitter / X",
    icon: faXTwitter,
    url: "https://x.com/poriya_jatin",
  },
  {
    name: "Instagram",
    icon: faInstagram,
    url: "https://www.instagram.com/jatin_poriya_404",
  },
  {
    name: "Email",
    icon: faEnvelope,
    url: "mailto:jatin@interestingplus.qzz.io",
  },
  {
    name: "Resume",
    icon: faFileLines,
    url: "https://drive.google.com/file/d/1R7l_wbETbET1Po6CpsOAfzwa3WPta7xU/view?usp=drive_link",
  },
];

// ==========================================
// ANIMATION VARIANTS
// ==========================================

const containerVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// ==========================================
// FOOTER COMPONENT
// ==========================================

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-[#020617] text-slate-200 overflow-hidden font-sans pt-24 pb-8 border-t border-white/5">
      {/* BACKGROUND GRID */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      {/* PURPLE GLOW */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[70vw] h-[30vw] bg-purple-600/10 rounded-full blur-[150px] opacity-40 pointer-events-none" />

      {/* TOP GRADIENT BORDER */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />

      <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-20 relative z-10">
        {/* MAIN FOOTER GRID */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            margin: "-50px",
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-20"
        >
          {/* ========================= */}
          {/* COLUMN 1 - BRAND */}
          {/* ========================= */}

          <motion.div
            variants={itemVariants}
            className="lg:col-span-4 flex flex-col items-center md:items-start text-center md:text-left"
          >
            {/* LOGO */}
            <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-purple-600/20 to-blue-600/20 border border-white/10 flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(147,51,234,0.1)]">
              <FontAwesomeIcon
                icon={faCode}
                className="text-xl text-purple-400"
              />
            </div>

            {/* NAME */}
            <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">
              Jatin Poriya
            </h2>

            {/* ROLES */}
            <p className="text-sm text-purple-400 font-medium tracking-wide uppercase mb-6">
              Full Stack Developer
              <span className="mx-2 text-white/20">•</span>
              Product Builder
              <span className="mx-2 text-white/20">•</span>
              Creative Problem Solver
            </p>

            {/* DESCRIPTION */}
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm mb-10 font-light">
              Building modern digital products, beautiful user experiences, and
              scalable software that solves real-world problems.
            </p>

            {/* POWERED BY BADGE */}
            {/* INTERESTING Plus Branding */}
            <div className="group bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 inline-block backdrop-blur-sm shadow-xl">
              <Link
                to="https://interestingplus.qzz.io"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="flex items-center gap-3">
                  {/* Logo */}
                  <div className="relative shrink-0">
                    <div className="absolute -inset-1 rounded-xl bg-gradient-to-r from-[#5B3DF5] to-[#00C2CB] opacity-40 blur-md group-hover:opacity-70 transition-opacity duration-300" />

                    <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-white/10 bg-slate-900 shadow-lg transition-transform duration-300 group-hover:scale-105">
                      <img
                        src="https://raw.githubusercontent.com/InterestingPlus/InterestingPlus/refs/heads/main/logo.jpg"
                        alt="Interesting Plus"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Branding */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[10px] font-semibold text-slate-400 tracking-[0.2em] uppercase">
                        Powered by
                      </span>
                      <Zap className="w-3.5 h-3.5 text-emerald-400 fill-emerald-400/20 animate-pulse" />
                    </div>

                    <div className="text-xl font-black tracking-tight text-white flex items-center gap-2">
                      INTERESTING
                      <span className="px-1.5 py-0.5 rounded-md bg-gradient-to-r from-[#5B3DF5] to-[#00C2CB] text-white text-sm shadow-lg">
                        Plus
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-slate-500 mt-3 italic group-hover:text-slate-300 transition-colors duration-300">
                  "Building Things That Are Actually Interesting."
                </div>
              </Link>
            </div>
          </motion.div>

          {/* ========================= */}
          {/* COLUMN 2 - QUICK LINKS */}
          {/* ========================= */}

          <motion.div
            variants={itemVariants}
            className="lg:col-span-2 flex flex-col items-center md:items-start text-center md:text-left"
          >
            <h3 className="text-sm font-bold text-white tracking-widest uppercase mb-8">
              Quick Links
            </h3>

            <ul className="flex flex-col gap-4">
              {quickLinks.map((link) => (
                <li key={link}>
                  <a
                    href={`/${link === "Home" ? "" : link.toLowerCase()}`}
                    className="group relative inline-block text-slate-400 hover:text-white transition-colors text-sm font-medium"
                  >
                    {link}

                    <span className="absolute -bottom-1 left-0 w-0 h-px bg-purple-500 transition-all duration-300 ease-out group-hover:w-full" />
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* ========================= */}
          {/* COLUMN 3 - RESOURCES */}
          {/* ========================= */}

          <motion.div
            variants={itemVariants}
            className="lg:col-span-3 flex flex-col items-center md:items-start text-center md:text-left"
          >
            <h3 className="text-sm font-bold text-white tracking-widest uppercase mb-8">
              Resources
            </h3>

            <ul className="flex flex-col gap-4">
              {resources.map((item) => (
                <li key={item.name}>
                  {item.soon ? (
                    <div className="flex items-center gap-3 text-sm font-medium text-slate-500 cursor-not-allowed">
                      <span>{item.name}</span>

                      <span className="text-[9px] uppercase tracking-wider font-bold bg-purple-500/10 border border-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                        Coming Soon
                      </span>
                    </div>
                  ) : (
                    <a
                      href={item.url}
                      className="group flex items-center gap-3 text-sm font-medium text-slate-400 hover:text-white transition-colors"
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* ========================= */}
          {/* COLUMN 4 - CONNECT */}
          {/* ========================= */}

          <motion.div
            variants={itemVariants}
            className="lg:col-span-3 flex flex-col items-center md:items-start text-center md:text-left"
          >
            <h3 className="text-sm font-bold text-white tracking-widest uppercase mb-8">
              Connect
            </h3>

            <ul className="flex flex-col gap-4 w-full max-w-[200px]">
              {socialConnects.map((social) => (
                <li key={social.name} className="w-full">
                  <a
                    href={social.url}
                    target={
                      social.url.startsWith("http") ? "_blank" : undefined
                    }
                    rel={
                      social.url.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="group flex items-center justify-between text-slate-400 hover:text-purple-400 transition-all duration-300 w-full"
                  >
                    <div className="flex items-center gap-3">
                      {/* FONT AWESOME ICON */}
                      <span className="w-5 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                        <FontAwesomeIcon
                          icon={social.icon}
                          className="w-4 h-4"
                          fixedWidth
                        />
                      </span>

                      {/* SOCIAL NAME */}
                      <span className="text-sm font-medium group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)] transition-all">
                        {social.name}
                      </span>
                    </div>

                    {/* HOVER ARROW */}
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="text-xs opacity-0 -translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>

        {/* ========================= */}
        {/* FOOTER BOTTOM */}
        {/* ========================= */}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.8,
            delay: 0.6,
          }}
          className="flex flex-col items-center"
        >
          {/* DIVIDER */}
          <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8" />

          <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
            {/* COPYRIGHT */}
            <p className="text-slate-500 text-sm font-medium tracking-wide">
              © {currentYear} Jatin Poriya. Designed and built with 💜
            </p>

            {/* TAGLINE */}
            <p className="text-slate-500 text-sm font-light italic">
              Turning ideas into products, and products into experiences.
            </p>
          </div>
        </motion.div>
      </div>

      {/* ANIMATED GRADIENT CSS */}
      <style>
        {`
          @keyframes gradient-x {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }

          .animate-gradient {
            animation: gradient-x 3s linear infinite;
          }
        `}
      </style>
    </footer>
  );
};

export default Footer;
